/**
 * PlacesSuggestInput — address input with Google Places suggestions rendered
 * by React directly under the field.
 *
 * Google's own widgets (SearchBox / Autocomplete) draw suggestions in a
 * body-level .pac-container that mobile browsers often hide or won't let you
 * tap. Rendering the list ourselves works the same on phones and desktop.
 *
 * Props:
 *   value              current text (controlled)
 *   onChange(text)     every keystroke, and the chosen suggestion's text
 *   onSelect(place)    after a suggestion is picked:
 *                      { formattedAddress, addressComponents, placeId, lat, lng }
 * Other props are passed to the Chakra Input.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Box, Input, List, ListItem, Text } from '@chakra-ui/react';
import { useGoogleMaps } from '../Contexts/GoogleMapsProvider';

const MIN_CHARS = 3;
const DEBOUNCE_MS = 250;
const DETAIL_FIELDS = ['formatted_address', 'address_components', 'geometry'];

const placesApi = () => window.google?.maps?.places;

export default function PlacesSuggestInput({
    value, onChange, onSelect, placeholder = 'Type and select an address', size = 'md', ...rest
}) {
    const { isLoaded } = useGoogleMaps();
    const [suggestions, setSuggestions] = useState([]);
    const [highlight, setHighlight] = useState(-1);
    const sessionRef = useRef(null);
    const timerRef = useRef(null);
    const requestRef = useRef(0);

    useEffect(() => () => clearTimeout(timerRef.current), []);

    const close = () => {
        setSuggestions([]);
        setHighlight(-1);
    };

    const fetchSuggestions = (text) => {
        clearTimeout(timerRef.current);
        const places = placesApi();
        if (!isLoaded || !places || text.trim().length < MIN_CHARS) {
            requestRef.current += 1;
            close();
            return;
        }
        timerRef.current = setTimeout(() => {
            if (!sessionRef.current) sessionRef.current = new places.AutocompleteSessionToken();
            const request = ++requestRef.current;
            new places.AutocompleteService().getPlacePredictions(
                { input: text, sessionToken: sessionRef.current },
                (predictions, status) => {
                    if (request !== requestRef.current) return;
                    setSuggestions(status === places.PlacesServiceStatus.OK && predictions ? predictions : []);
                    setHighlight(-1);
                },
            );
        }, DEBOUNCE_MS);
    };

    const choose = (prediction) => {
        clearTimeout(timerRef.current);
        requestRef.current += 1;
        close();
        onChange(prediction.description);
        if (typeof onSelect !== 'function') return;

        const fallback = {
            formattedAddress: prediction.description, addressComponents: [],
            placeId: prediction.place_id, lat: null, lng: null,
        };
        const places = placesApi();
        const sessionToken = sessionRef.current;
        sessionRef.current = null;
        if (!places) {
            onSelect(fallback);
            return;
        }
        new places.PlacesService(document.createElement('div')).getDetails(
            { placeId: prediction.place_id, fields: DETAIL_FIELDS, sessionToken },
            (place, status) => {
                if (status !== places.PlacesServiceStatus.OK || !place) {
                    onSelect(fallback);
                    return;
                }
                const location = place.geometry?.location;
                onSelect({
                    formattedAddress: place.formatted_address || prediction.description,
                    addressComponents: place.address_components || [],
                    placeId: prediction.place_id,
                    lat: location ? location.lat() : null,
                    lng: location ? location.lng() : null,
                });
            },
        );
    };

    const handleKeyDown = (e) => {
        if (!suggestions.length) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((h) => (h + 1) % suggestions.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => (h <= 0 ? suggestions.length - 1 : h - 1));
        } else if (e.key === 'Enter' && highlight >= 0) {
            e.preventDefault();
            choose(suggestions[highlight]);
        } else if (e.key === 'Escape') {
            close();
        }
    };

    return (
        <Box position="relative" width="100%">
            <Input
                type="text"
                value={value}
                onChange={(e) => {
                    onChange(e.target.value);
                    fetchSuggestions(e.target.value);
                }}
                onKeyDown={handleKeyDown}
                onBlur={() => setTimeout(close, 150)}
                placeholder={placeholder}
                size={size}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                {...rest}
            />
            {suggestions.length > 0 && (
                <Box
                    position="absolute"
                    top="100%"
                    left={0}
                    right={0}
                    mt={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    boxShadow="lg"
                    zIndex={1500}
                    maxH="260px"
                    overflowY="auto"
                    textAlign="left"
                >
                    <List role="listbox">
                        {suggestions.map((s, i) => (
                            <ListItem
                                key={s.place_id}
                                role="option"
                                aria-selected={i === highlight}
                                px={3}
                                py={3}
                                fontSize="sm"
                                color="gray.800"
                                cursor="pointer"
                                bg={i === highlight ? 'blue.50' : 'white'}
                                _hover={{ bg: 'blue.50' }}
                                borderBottom="1px solid"
                                borderColor="gray.100"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => choose(s)}
                            >
                                {s.description}
                            </ListItem>
                        ))}
                    </List>
                    <Text fontSize="10px" color="gray.500" textAlign="right" px={3} py={1}>
                        Powered by Google
                    </Text>
                </Box>
            )}
        </Box>
    );
}
